import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../utils/date";
import AppShell from "../components/AppShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Eye, EyeOff, Check, X, KeyRound, ShieldCheck, UserCircle2, Smartphone, Laptop, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { ROLE_LABELS } from "../lib/rbac";
import { toast } from "sonner";

// Iter75.2 — Password policy Tier 1 (12 chars + upper/lower/digit/symbol).
// Kept in sync with `/app/backend/security.py::validate_password_strength`.
const ALL_RULES = [
    { key: "min_length", label: "At least 12 characters" },
    { key: "uppercase", label: "At least one uppercase letter (A–Z)" },
    { key: "lowercase", label: "At least one lowercase letter (a–z)" },
    { key: "digit", label: "At least one digit (0–9)" },
    { key: "special", label: "At least one special character (!@#$%^&* etc.)" },
];

const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/]/;

function evalRules(pwd) {
    return {
        min_length: pwd.length >= 12,
        uppercase: /[A-Z]/.test(pwd),
        lowercase: /[a-z]/.test(pwd),
        digit: /[0-9]/.test(pwd),
        special: SPECIAL_RE.test(pwd),
    };
}

function PasswordField({ id, label, value, onChange, testid, autoComplete }) {
    const [show, setShow] = useState(false);
    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <div className="relative">
                <Input
                    id={id}
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    className="pr-10"
                    data-testid={testid}
                />
                <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--asl-muted)] hover:asl-navy-text"
                    data-testid={`${testid}-toggle`}
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
        </div>
    );
}

export default function AccountSettings() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const { byId: modulesById } = useModules();
    const [me, setMe] = useState(null);
    const [mfaStatus, setMfaStatus] = useState(null);
    const [cur, setCur] = useState("");
    const [n1, setN1] = useState("");
    const [n2, setN2] = useState("");
    const [busy, setBusy] = useState(false);
    // Iter81 — Trusted-device list state.
    const [trustedDevices, setTrustedDevices] = useState([]);
    const [trustedBusy, setTrustedBusy] = useState(false);

    const loadTrustedDevices = React.useCallback(() => {
        api.get("/auth/trusted-devices")
            .then(({ data }) => setTrustedDevices(data?.devices || []))
            .catch(() => setTrustedDevices([]));
    }, []);

    useEffect(() => {
        api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
        api.get("/auth/mfa/status").then(({ data }) => setMfaStatus(data)).catch(() => {});
        loadTrustedDevices();
    }, [loadTrustedDevices]);

    const revokeOneDevice = async (id) => {
        setTrustedBusy(true);
        try {
            await api.delete(`/auth/trusted-devices/${id}`);
            toast.success("Trusted device revoked");
            loadTrustedDevices();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTrustedBusy(false);
        }
    };
    const revokeAllDevices = async () => {
        setTrustedBusy(true);
        try {
            const { data } = await api.post("/auth/trusted-devices/revoke-all");
            toast.success(`Revoked ${data?.revoked_count ?? 0} trusted device(s)`);
            loadTrustedDevices();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTrustedBusy(false);
        }
    };

    const rules = useMemo(() => evalRules(n1), [n1]);
    const allRulesPass = Object.values(rules).every(Boolean);
    const passwordsMatch = n1.length > 0 && n1 === n2;
    const notSameAsCurrent = n1.length === 0 || n1 !== cur;
    const canSubmit = cur.length > 0 && allRulesPass && passwordsMatch && notSameAsCurrent && !busy;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: cur,
                new_password: n1,
                confirm_password: n2,
            });
            toast.success("Password updated. Your session remains active.");
            setCur("");
            setN1("");
            setN2("");
            refresh();
            api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const moduleName = me?.module_scope ? modulesById[me.module_scope]?.name : null;

    return (
        <AppShell title="My Account">
            <section className="space-y-6 max-w-4xl" data-testid="account-page">
                <header className="flex items-center gap-3">
                    <UserCircle2 className="h-7 w-7 asl-navy-text" />
                    <div>
                        <h2 className="text-xl font-bold asl-navy-text">My Account</h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            View your profile and change your password.
                        </p>
                    </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="asl-card p-6 space-y-3" data-testid="account-profile-card">
                        <div className="flex items-center gap-2 mb-1">
                            <UserCircle2 className="h-5 w-5 asl-navy-text" />
                            <h3 className="text-base font-semibold asl-navy-text">Profile</h3>
                        </div>
                        <ProfileRow label="Name" value={me?.name || user?.name || "—"} testid="profile-name" />
                        <ProfileRow label="Email" value={me?.email || user?.email || "—"} testid="profile-email" />
                        <ProfileRow
                            label="Role"
                            value={ROLE_LABELS[me?.role || user?.role] || me?.role || user?.role || "—"}
                            testid="profile-role"
                        />
                        <ProfileRow
                            label="Module scope"
                            value={moduleName || "All modules"}
                            testid="profile-module"
                        />
                    </div>

                    <div className="asl-card p-6 space-y-3" data-testid="account-sessions-card">
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldCheck className="h-5 w-5 asl-navy-text" />
                            <h3 className="text-base font-semibold asl-navy-text">Sessions &amp; security</h3>
                        </div>
                        <ProfileRow
                            label="Last login"
                            value={me?.last_login_at ? formatDateTime(me.last_login_at) : "—"}
                            testid="session-last-login"
                        />
                        <ProfileRow
                            label="Password last changed"
                            value={me?.password_changed_at ? formatDateTime(me.password_changed_at) : "Never"}
                            testid="session-password-changed"
                        />
                        <p className="text-xs text-[color:var(--asl-muted)] pt-2">
                            For security, you&apos;ll be signed out automatically after 10 minutes of inactivity,
                            and your session ends after 8 hours regardless of activity.
                        </p>
                    </div>
                </div>

                {/* Iter75.7 — MFA management. */}
                <div className="asl-card p-6 space-y-4" data-testid="account-mfa-card">
                    <div className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5 asl-navy-text" />
                        <h3 className="text-base font-semibold asl-navy-text">Two-factor authentication</h3>
                        {mfaStatus?.mfa_enabled ? (
                            <span
                                className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: "#DCFCE7", color: "#166534" }}
                                data-testid="account-mfa-badge-enabled"
                            >
                                Enabled
                            </span>
                        ) : (
                            <span
                                className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: "#FEF3C7", color: "#92400E" }}
                                data-testid="account-mfa-badge-disabled"
                            >
                                Not enabled
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-[color:var(--asl-muted)]">
                        {mfaStatus?.mfa_enabled
                            ? `Your account is protected by an authenticator app. Backup codes remaining: ${mfaStatus?.backup_codes_remaining ?? 0}.`
                            : "Protect your account with a time-based code from Google Authenticator, Authy, or 1Password."}
                    </p>
                    {mfaStatus?.mfa_enforced && !mfaStatus?.mfa_enabled && (mfaStatus?.grace_remaining_seconds ?? 0) > 0 && (
                        <div
                            className="rounded-md border px-3 py-2 text-xs"
                            style={{ background: "#FFF7E6", borderColor: "#F5C87B", color: "#7A4B00" }}
                            data-testid="account-mfa-grace-banner"
                        >
                            MFA is required. You have{" "}
                            {Math.max(1, Math.ceil((mfaStatus.grace_remaining_seconds || 0) / 86400))}{" "}
                            day(s) left to enroll.
                        </div>
                    )}
                    {!mfaStatus?.mfa_enabled ? (
                        <Button
                            type="button"
                            className="asl-navy-bg text-white"
                            onClick={() => navigate("/setup-mfa")}
                            data-testid="account-mfa-setup-button"
                        >
                            <Smartphone className="h-4 w-4 mr-1.5" />
                            Set up two-factor authentication
                        </Button>
                    ) : null}
                </div>

                {/* Iter81 — Trusted devices management. */}
                <div className="asl-card p-6 space-y-4" data-testid="account-trusted-devices-card">
                    <div className="flex items-center gap-2">
                        <Laptop className="h-5 w-5 asl-navy-text" />
                        <h3 className="text-base font-semibold asl-navy-text">Trusted devices</h3>
                        <span className="ml-auto text-xs text-[color:var(--asl-muted)]" data-testid="trusted-devices-count">
                            {trustedDevices.length} active
                        </span>
                    </div>
                    <p className="text-sm text-[color:var(--asl-muted)]">
                        Devices where you've opted to skip the 6-digit code for 7 days after a successful login.
                        Any password change, admin action, or suspicious activity automatically revokes all trusted devices.
                    </p>
                    {trustedDevices.length === 0 ? (
                        <div className="rounded-md p-3 text-xs text-[color:var(--asl-muted)]"
                            style={{ background: "var(--asl-bg,#F5F6F8)" }}
                            data-testid="trusted-devices-empty">
                            No trusted devices right now. Check the "Trust this device for 7 days" box on the
                            two-factor screen next time you log in to add one.
                        </div>
                    ) : (
                        <>
                            <ul className="divide-y" style={{ borderColor: "var(--asl-border)" }} data-testid="trusted-devices-list">
                                {trustedDevices.map((d) => (
                                    <li key={d.id} className="py-2.5 flex items-center gap-3 text-sm"
                                        data-testid={`trusted-device-row-${d.id}`}>
                                        <Laptop className="h-4 w-4 asl-navy-text shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium asl-navy-text truncate">{d.label || "Unknown device"}</div>
                                            <div className="text-xs text-[color:var(--asl-muted)]">
                                                Trusted until {formatDateTime(d.expires_at)} · last seen {formatDateTime(d.last_seen_at)}
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => revokeOneDevice(d.id)}
                                            disabled={trustedBusy}
                                            title="Revoke this device"
                                            data-testid={`trusted-device-revoke-${d.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-rose-600" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={revokeAllDevices}
                                disabled={trustedBusy}
                                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                                data-testid="trusted-devices-revoke-all"
                            >
                                <Trash2 className="h-4 w-4 mr-1.5" />
                                Revoke all trusted devices
                            </Button>
                        </>
                    )}
                </div>

                <form onSubmit={submit} className="asl-card p-6 space-y-4 max-w-xl" data-testid="change-password-form">
                    <div className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5 asl-navy-text" />
                        <h3 className="text-base font-semibold asl-navy-text">Change password</h3>
                    </div>

                    <PasswordField
                        id="current_password"
                        label="Current password"
                        value={cur}
                        onChange={setCur}
                        autoComplete="current-password"
                        testid="cp-current"
                    />
                    <PasswordField
                        id="new_password"
                        label="New password"
                        value={n1}
                        onChange={setN1}
                        autoComplete="new-password"
                        testid="cp-new"
                    />
                    <PasswordField
                        id="confirm_password"
                        label="Confirm new password"
                        value={n2}
                        onChange={setN2}
                        autoComplete="new-password"
                        testid="cp-confirm"
                    />

                    <ul className="rounded-md p-3 space-y-1 text-xs" style={{ background: "var(--asl-bg,#F5F6F8)" }}
                        data-testid="cp-rules">
                        {ALL_RULES.map((r) => {
                            const ok = rules[r.key];
                            return (
                                <li key={r.key}
                                    className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-[color:var(--asl-muted)]"}`}
                                    data-testid={`cp-rule-${r.key}-${ok ? "ok" : "fail"}`}>
                                    {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                                    <span>{r.label}</span>
                                </li>
                            );
                        })}
                        <li className={`flex items-center gap-2 ${passwordsMatch ? "text-emerald-700" : "text-[color:var(--asl-muted)]"}`}
                            data-testid={`cp-rule-match-${passwordsMatch ? "ok" : "fail"}`}>
                            {passwordsMatch ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            <span>Confirmation matches the new password</span>
                        </li>
                        <li className={`flex items-center gap-2 ${notSameAsCurrent ? "text-emerald-700" : "text-rose-700"}`}
                            data-testid={`cp-rule-different-${notSameAsCurrent ? "ok" : "fail"}`}>
                            {notSameAsCurrent ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            <span>New password is different from the current one</span>
                        </li>
                    </ul>

                    <Button type="submit" disabled={!canSubmit}
                        className="asl-navy-bg text-white"
                        data-testid="cp-submit">
                        {busy ? "Updating…" : "Update password"}
                    </Button>
                </form>
            </section>
        </AppShell>
    );
}

function ProfileRow({ label, value, testid }) {
    return (
        <div className="flex justify-between gap-3 text-sm border-b last:border-b-0 py-1.5"
            style={{ borderColor: "var(--asl-border)" }}>
            <span className="text-[color:var(--asl-muted)]">{label}</span>
            <span className="asl-navy-text font-medium text-right truncate" data-testid={testid}>{value}</span>
        </div>
    );
}
